import api from '@api';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import {
    addOrderProductsFetchFailure,
    addOrderProductsFetchSuccess,
    ordersChangeStatusFetchFailure,
    ordersChangeStatusFetchSuccess,
    patchMultipleOrdersFetchFailure,
    patchMultipleOrdersFetchSuccess,
    patchOrderFetchFailure,
    patchOrderFetchSuccess, removeOrderProductsFetchFailure, removeOrderProductsFetchSuccess,
    reorderMultipleOrdersShippingFetchFailure,
    reorderMultipleOrdersShippingFetchSuccess,
    reorderOrderShippingFetchFailure,
    reorderOrderShippingFetchSuccess,
    synchronizeOrdersDCIXFetchFailure,
    synchronizeOrdersDCIXFetchSuccess
} from './reducer';

import { getOrdersGridFetch } from '../ordersGrid/reducer';
import { OrderProductType, OrderType } from 'src/types/order';
import translate from '@translate';
import { CANCEL_REASON_ORDER_STATES } from '../../containers/AllOrdersContainer';

const patchOrderFetchRequest = async (order: OrderType, data: OrderType) => {
    const res = await api('patch', `api/secure/orders/${order.ID}?system=myskladon`, data);
    return res.data;
};

function* patchOrderFetch(action: { type: string; payload: { order: OrderType; data: OrderType } }) {
    const { order, data } = action.payload;

    try {
        yield call((): any => patchOrderFetchRequest(order, data));
        yield put(patchOrderFetchSuccess());
    } catch (error) {
        yield put(patchOrderFetchFailure({ error }));
    }
}

export function* patchOrderFetchWatch() {
    yield takeEvery('ordersUpdate/patchOrderFetch', patchOrderFetch);
}

function* patchMultipleOrdersFetch(action: { type: string; payload: { orders: OrderType[]; data: OrderType } }) {
    const { orders, data } = action.payload;
    const batchSize = 10; // Nastav velikost dávky

    try {
        // Rozdělit orders na menší dávky
        const chunks = [];
        for (let i = 0; i < orders.length; i += batchSize) {
            chunks.push(orders.slice(i, i + batchSize));
        }

        // Zpracovat každou dávku samostatně
        for (const chunk of chunks) {
            yield all(
                chunk.map((order) =>
                    call(patchOrderFetch, {
                        type: 'ordersUpdate/patchOrderFetch',
                        payload: {
                            order,
                            data: {
                                ...data,
                                CANCEL_REASON: !CANCEL_REASON_ORDER_STATES.includes(order.STATUS!) ? undefined : data.CANCEL_REASON
                            }
                        }
                    })
                )
            );
        }

        yield put(patchMultipleOrdersFetchSuccess());
        yield put(getOrdersGridFetch(undefined));
    } catch (error) {
        yield put(patchMultipleOrdersFetchFailure({ error }));
        yield put(getOrdersGridFetch(undefined));
    }
}

export function* patchMultipleOrdersFetchWatch() {
    yield takeEvery('ordersUpdate/patchMultipleOrdersFetch', patchMultipleOrdersFetch);
}

const addOrderProductsFetchRequest = async (data: { orderIds: number[], orderProducts: OrderProductType[], addProductsIfExists: boolean }) => {
    const res = await api('post', `api/secure/orders/add-order-products?system=myskladon`, data);
    return res.data;
};

function* addOrderProductsFetch(action: {
    type: string;
    payload: { orderIds: number[]; orderProducts: OrderProductType[], addProductsIfExists: boolean }
}) {
    try {
        yield call((): any => addOrderProductsFetchRequest(action.payload));
        yield put(addOrderProductsFetchSuccess({ messageSuccess: { title: translate('orders.message.productsAddedToOrders') } }));
    } catch (error) {
        yield put(addOrderProductsFetchFailure({ error }));
    }
}

export function* addOrderProductsFetchWatch() {
    yield takeEvery('ordersUpdate/addOrderProductsFetch', addOrderProductsFetch);
}

const removeOrderProductsFetchRequest = async (data: { orderIds: number[], productIds: number[] }) => {
    const res = await api('put', `api/secure/orders/remove-order-products?system=myskladon`, data);
    return res.data;
};

function* removeOrderProductsFetch(action: {
    type: string;
    payload: { orderIds: number[]; productIds: number[] }
}) {
    try {
        yield call((): any => removeOrderProductsFetchRequest(action.payload));
        yield put(removeOrderProductsFetchSuccess({ messageSuccess: { title: translate('orders.message.productsRemovedFromOrders') } }));
    } catch (error) {
        yield put(removeOrderProductsFetchFailure({ error }));
    }
}

export function* removeOrderProductsFetchWatch() {
    yield takeEvery('ordersUpdate/removeOrderProductsFetch', removeOrderProductsFetch);
}

const ordersChangeStatusFetchRequest = async (id: number, STATUS: string) => {
    const res = await api('patch', `api/secure/orders/${id}?system=myskladon`, { STATUS });
    return res.data;
};

function* ordersChangeStatusFetch(action: { type: string; payload: OrderType }) {
    const { ID, STATUS } = action.payload;

    try {
        if (ID && STATUS) {
            yield call((): any => ordersChangeStatusFetchRequest(ID, STATUS));
        }
        yield put(ordersChangeStatusFetchSuccess());
    } catch (error) {
        yield put(ordersChangeStatusFetchFailure({ error }));
    }
}

export function* ordersChangeStatusFetchWatch() {
    yield takeEvery('ordersUpdate/ordersChangeStatusFetch', ordersChangeStatusFetch);
}

const reorderOrderShippingFetchRequest = async (id: number) => {
    const res = await api('get', `api/secure/orders/create-shipping/${id}`);
    return res.data;
};

function* reorderOrderShippingFetch(action: {
    type: string;
    payload: { order: OrderType; callback?: (succes: boolean) => void };
}) {
    const order = action.payload.order;
    const id = Number(order.ID);

    try {
        yield call((): any => reorderOrderShippingFetchRequest(id));
        yield put(reorderOrderShippingFetchSuccess());
        if (action.payload.callback) {
            action.payload.callback(true);
        }
    } catch (error) {
        yield put(reorderOrderShippingFetchFailure({ error }));
        if (action.payload.callback) {
            action.payload.callback(false);
        }
    }
}

export function* reorderOrderShippingFetchWatch() {
    yield takeEvery('ordersUpdate/reorderOrderShippingFetch', reorderOrderShippingFetch);
}

function* reorderMultipleOrdersShippingFetch(action: { type: string; payload: OrderType[] }) {
    try {
        yield all(
            action.payload.map((order) =>
                call(reorderOrderShippingFetch, { type: 'ordersUpdate/reorderOrderShippingFetch', payload: { order } })
            )
        );
        yield put(reorderMultipleOrdersShippingFetchSuccess());
        yield put(getOrdersGridFetch(undefined));
    } catch (error) {
        yield put(reorderMultipleOrdersShippingFetchFailure({ error }));
        yield put(getOrdersGridFetch(undefined));
    }
}

export function* reorderMultipleOrdersShippingFetchWatch() {
    yield takeEvery('ordersUpdate/reorderMultipleOrdersShippingFetch', reorderMultipleOrdersShippingFetch);
}

const synchronizeOrdersDCIXFetchRequest = async (orders: OrderType[]) => {
    const res = await api('post', `api/secure/dcix/import/orders`, { keys: orders.map((order) => order.ID) });
    return res.data;
};

function* synchronizeOrdersDCIXFetch(action: { type: string; payload: OrderType[] }) {
    try {
        yield call((): any => synchronizeOrdersDCIXFetchRequest(action.payload));
        yield put(
            synchronizeOrdersDCIXFetchSuccess({
                messageSuccess: { title: translate('orders.message.synchronizeSuccessfull') }
            })
        );
        yield put(getOrdersGridFetch(undefined));
    } catch (error) {
        yield put(synchronizeOrdersDCIXFetchFailure({ error }));
        yield put(getOrdersGridFetch(undefined));
    }
}

export function* synchronizeOrdersDCIXFetchWatch() {
    yield takeEvery('ordersUpdate/synchronizeOrdersDCIXFetch', synchronizeOrdersDCIXFetch);
}
